import React, { useState, useEffect } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import enGB from "date-fns/locale/en-GB";
import { TextField, Skeleton } from "@mui/material";
import useTitanApp from "../../../../../hooks/useTitanApp";
import CustomButton from "../../../../controls/CustomButton";
import { Dropdown } from "primereact/dropdown";
import { GoPlus, GoPlusCircle } from "react-icons/go";
import EmailChipsInput from "../../../PatientConsultationUtils/EmailChipsInput";
import {
  getAppointmentsByPharmacyId,
  savePatientAppointment,
  updateConsultationActions,
  setIncrementBookingCompleted,
  setAddPatientAIChatMessage,
} from "../../../../../dispatchers/patientDispatchers";
import { MdOutlineDelete } from "react-icons/md";
import { IoClose } from "react-icons/io5";
import { ActionType } from "../../../../../enum/ActionType";

const darkTheme = createTheme({
  palette: {
    mode: "dark",
  },
});

function PatientBooking({ handleClose }) {
  const { appState, patientState } = useTitanApp();
  const {
    usersAvailability,
    selectedPatientDetails,
    usersAvailabilityLoader,
    consultationId,
    bookingCompleted,
    bookingDetails,
  } = patientState || {};
  const { patientID } = selectedPatientDetails || {};
  const { darkMode, pharmacies } = appState || {};
  const [bookingForms, setBookingForms] = useState([
    {
      formData: { appointmentName: "", appointmentDate: "" /* , type: "" */ },
      selectedPharmacy: null,
      selectedPractitioner: null,
      // selectedTemplate: null,
      availableSlots: [],
      selectedSlot: null,
      // templates: [],
      errors: {},
    },
  ]);
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [practitioners, setPractitioners] = useState([]);

  const filteredBookingForms = bookingForms.map(
    ({ availableSlots, ...rest }) => rest
  );

  const appointmentPayload = {
    payload: JSON.stringify({
      bookingForms: filteredBookingForms,
      emailRecipients,
    }),
    actionName: ActionType[4],
    actionNumber: "4",
    isPerformed: true,
    image: "",
  };
  const handleActionSave = async () => {
    await updateConsultationActions({
      consultationId: consultationId,
      payload: appointmentPayload,
    }).then(() => {
      if (bookingCompleted) {
        setAddPatientAIChatMessage({
          sender: "success",
          message: "Booking completed successfully.",
          actionId: 4,
          details: bookingDetails,
        });
      }
    });
  };

  useEffect(() => {
    if (usersAvailability && Array.isArray(usersAvailability)) {
      const practitionerList = usersAvailability.map((item) => ({
        label: item.user.fullName,
        value: item.user.userId,
      }));
      setPractitioners(practitionerList);
    }
  }, [usersAvailability]);

  const getWeekdayName = (dateString) => {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    return days[new Date(dateString).getDay()];
  };

  const handleAddAnother = () => {
    setBookingForms((prev) => [
      ...prev,
      {
        formData: { appointmentName: "", appointmentDate: "" /* , type: "" */ },
        selectedPharmacy: null,
        selectedPractitioner: null,
        // selectedTemplate: null,
        availableSlots: [],
        selectedSlot: null,
        // templates: [],
        errors: {},
      },
    ]);
  };

  const handleSubmit = async () => {
    let hasError = false;

    for (let i = 0; i < bookingForms.length; i++) {
      const form = bookingForms[i];
      const newErrors = {};

      if (!form.formData.appointmentName)
        newErrors.appointmentName = "Required";
      if (!form.selectedPharmacy) newErrors.pharmacy = "Required";
      if (!form.selectedPractitioner) newErrors.practitioner = "Required";
      // if (!form.selectedTemplate) newErrors.template = "Required";
      if (!form.formData.appointmentDate)
        newErrors.appointmentDate = "Required";
      if (!form.selectedSlot) newErrors.slot = "Required";
      // if (!form.formData.type) newErrors.type = "Required";

      if (Object.keys(newErrors).length > 0) {
        hasError = true;
        setBookingForms((prev) => {
          const updated = [...prev];
          updated[i].errors = newErrors;
          return updated;
        });
      }
    }

    if (hasError) {
      console.error("❌ Missing required fields in one or more forms");
      return;
    }

    const allBookingData = bookingForms.map((form) => {
      const parts = form.selectedSlot.split("-");
      const startTime = parts[0]?.slice(0, 5);
      const endTime = parts[1]?.slice(0, 5);

      const practitioner = usersAvailability.find(
        (item) => item.user.userId === form.selectedPractitioner
      );
      const practitionerEmail = practitioner?.user?.emailAddress || "";

      return {
        // serviceId: form.selectedTemplate,
        appointmentName: form.formData.appointmentName,
        patientId: patientID || "PatientIdHere",
        appointmentDate:
          form.formData.appointmentDate && startTime
            ? `${form.formData.appointmentDate}T${startTime}:00Z`
            : "",
        pharmacyID: form.selectedPharmacy,
        practitionerEmail,
        consultationId: consultationId,
        // type: form.formData.type,
        email: emailRecipients,
        startTime,
        endTime,
        roomID: "",
      };
    });
    handleActionSave();
    // Optionally submit in batch:
    await savePatientAppointment(allBookingData).then((response) => {
      if (response === true) {
        setIncrementBookingCompleted(true);
      }
    });
    handleClose();
  };

  const handleDeleteBooking = (index) => {
    if (index === 0) return; // Prevent deleting first booking
    setBookingForms((prev) => prev.filter((_, i) => i !== index));
  };
  const isDisabled = !consultationId || consultationId.trim() === "";
  return (
    <div className="pc-actions-modal-wrapper">
      <div className="pc-actions-modal-header">
        <h3>Book appointment</h3>
        <IoClose
          className="close-icon"
          onClick={handleClose}
          color={darkMode ? "#f2f2f2" : "#222"}
        />
      </div>

      <div className="pc-actions-modal-content two-cols">
        <div className="left">
          <div className="prescription-items-wrapper">
            {bookingForms.map((form, index) => (
              <div className="booking-form" key={index}>
                <div>
                  {index !== 0 && (
                    <MdOutlineDelete
                      size={20}
                      className="icons-color icons-pointer"
                      onClick={() => handleDeleteBooking(index)}
                      title="Delete this booking"
                    />
                  )}
                </div>
                <div className="pc-modal-input-field">
                  <label>Appointment Name</label>
                  <TextField
                    fullWidth
                    required
                    value={form.formData.appointmentName}
                    error={!!form.errors.appointmentName}
                    helperText={form.errors.appointmentName}
                    onChange={(e) => {
                      const updated = [...bookingForms];
                      updated[index].formData.appointmentName = e.target.value;
                      setBookingForms(updated);
                    }}
                    InputLabelProps={{ sx: { color: "#999999" } }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "16px",
                        backgroundColor: darkMode ? "#111" : "#fff",
                      },
                      "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                        borderColor: "#484848",
                      },
                    }}
                  />
                </div>

                <div className="pc-modal-input-field">
                  <label>
                    {form.errors.pharmacy
                      ? "⚠ Pharmacy (Required)"
                      : "Pharmacy"}
                  </label>

                  <Dropdown
                    value={form.selectedPharmacy}
                    onChange={async (e) => {
                      const updated = [...bookingForms];
                      updated[index].selectedPharmacy = e.value;
                      setBookingForms(updated);
                      await getAppointmentsByPharmacyId(e.value);
                    }}
                    options={pharmacies || []}
                    optionLabel="teamPharmacyName"
                    optionValue="teamPharmacyID"
                    className="dropdown"
                    panelClassName="bucket-dropdown-panel"
                  />
                </div>

                <div className="pc-modal-input-field">
                  <label>
                    {form.errors.practitioner
                      ? "⚠ Practitioner (Required)"
                      : "Practitioner"}
                  </label>

                  {usersAvailabilityLoader ? (
                    <Skeleton
                      sx={{
                        bgcolor: darkMode ? "grey.900" : "",
                        borderRadius: "8px",
                      }}
                      animation="wave"
                      variant="rounded"
                      height={35}
                    />
                  ) : (
                    <Dropdown
                      value={form.selectedPractitioner}
                      onChange={(e) => {
                        const updated = [...bookingForms];
                        updated[index].selectedPractitioner = e.value;
                        const practitioner = usersAvailability.find(
                          (item) => item.user.userId === e.value
                        );
                        updated[index].templates =
                          practitioner?.user?.services?.map((service) => ({
                            label: service.serviceName,
                            value: service._id,
                          })) || [];
                        setBookingForms(updated);
                      }}
                      options={practitioners}
                      optionLabel="label"
                      className="dropdown"
                      panelClassName="bucket-dropdown-panel"
                      disabled={!form.selectedPharmacy}
                    />
                  )}
                </div>

                <div className="pc-modal-input-field date_controller_wrapper">
                  <label>Date</label>
                  <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={enGB}
                  >
                    <DatePicker
                      value={
                        form.formData.appointmentDate
                          ? new Date(form.formData.appointmentDate)
                          : null
                      }
                      onChange={(newValue) => {
                        const isValidDate =
                          newValue instanceof Date &&
                          !isNaN(newValue.getTime());
                        // const formattedDate = isValidDate
                        //   ? newValue.toISOString().split("T")[0]
                        //   : "";
                        const formattedDate = isValidDate
                          ? newValue.toLocaleDateString("en-CA")
                          : "";
                        const updated = [...bookingForms];
                        updated[index].formData.appointmentDate = formattedDate;

                        if (isValidDate && form.selectedPractitioner) {
                          const practitioner = usersAvailability.find(
                            (item) =>
                              item.user.userId === form.selectedPractitioner
                          );
                          if (practitioner?.freeSlots) {
                            const weekdayName = getWeekdayName(newValue);
                            const slots =
                              practitioner.freeSlots[weekdayName] || [];
                            updated[index].availableSlots = slots.map(
                              (slot, idx) => ({
                                label: `${slot.start} - ${slot.end}`,
                                value: `${slot.start}-${slot.end}-${idx}`,
                              })
                            );
                          } else {
                            updated[index].availableSlots = [];
                          }
                        } else {
                          updated[index].availableSlots = [];
                        }
                        setBookingForms(updated);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: !!form.errors.appointmentDate,
                          helperText: form.errors.appointmentDate,
                          variant: "outlined",
                          onKeyDown: (e) => e.preventDefault(),
                          InputProps: {
                            sx: { borderRadius: "16px" },
                          },
                          InputLabelProps: { sx: { color: "#999999" } },
                          sx: {
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "16px",
                              backgroundColor: darkMode ? "#111" : "#fff",
                            },
                            "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                              borderColor: "#484848",
                            },
                          },
                        },
                      }}
                      shouldDisableDate={(date) => {
                        if (date < new Date().setHours(0, 0, 0, 0)) {
                          return true;
                        }

                        const practitioner = usersAvailability.find(
                          (item) =>
                            item.user.userId === form.selectedPractitioner
                        );
                        if (!practitioner?.freeSlots) {
                          return false;
                        }

                        const weekdayName = getWeekdayName(date.toISOString());
                        const slots = practitioner.freeSlots[weekdayName];
                        if (!slots || slots.length === 0) {
                          return true;
                        }
                        return false;
                      }}
                      disabled={!form.selectedPractitioner}
                    />
                  </LocalizationProvider>
                </div>

                {form.availableSlots.length > 0 && (
                  <div className="pc-modal-input-field slots_dropdown">
                    <label>
                      {form.errors.slot ? "⚠ Slot (Required)" : "Slots"}
                    </label>

                    <Dropdown
                      value={form.selectedSlot}
                      onChange={(e) => {
                        const updated = [...bookingForms];
                        updated[index].selectedSlot = e.value;
                        setBookingForms(updated);
                      }}
                      options={form.availableSlots}
                      optionLabel="label"
                      className="dropdown"
                      panelClassName="bucket-dropdown-panel"
                    />
                  </div>
                )}

                {/* <div className="booking_type_template_wrapper">
                  <div className="booking_type_container">
                    <TextField
                      label="Type"
                      fullWidth
                      required
                      value={form.formData.type}
                      error={!!form.errors.type}
                      helperText={form.errors.type}
                      onChange={(e) => {
                        const updated = [...bookingForms];
                        updated[index].formData.type = e.target.value;
                        setBookingForms(updated);
                      }}
                      InputLabelProps={{ sx: { color: "#999999" } }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "16px",
                          backgroundColor: darkMode ? "#111" : "#fff",
                        },
                        "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                          borderColor: "#484848",
                        },
                      }}
                    />
                  </div>

                  <div className="booking_template_container">
                    <Dropdown
                      value={form.selectedTemplate}
                      onChange={(e) => {
                        const updated = [...bookingForms];
                        updated[index].selectedTemplate = e.value;
                        setBookingForms(updated);
                      }}
                      options={form.templates}
                      optionLabel="label"
                      placeholder={
                        form.errors.template
                          ? "⚠ Template (Required)"
                          : "Template"
                      }
                      className="dropdown"
                      panelClassName="bucket-dropdown-panel"
                    />
                  </div>
                </div> */}
              </div>
            ))}

            <div className="add-template-btn" onClick={handleAddAnother}>
              <GoPlus className="icons-pointer" fill="#999999" size={16} />
              Add another booking
            </div>
          </div>
        </div>
        <div className="right">
          <div className="index">Options</div>
          <EmailChipsInput
            value={emailRecipients}
            onChange={setEmailRecipients}
            darkMode={darkMode}
          />

          <div className="action-buttons">
            <button className="cancel" onClick={handleClose}>
              Cancel
            </button>
            <button
              className="done"
              disabled={isDisabled}
              onClick={(e) => {
                if (isDisabled) {
                  e.preventDefault();
                  return;
                }
                handleSubmit();
              }}
            >
              Create Booking
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PatientBooking;
